import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { RPCResult } from '@campaign-portal/namespace/common/rpc.response';
import { exist } from '@campaign-portal/namespace/common/id';
import { Package, Plan } from '@campaign-portal/namespace/entities/subscriptions/specs';
import { SubscriptionType, TrafficType } from '@campaign-portal/namespace/common/enums';
import {
	AlarisAlertComponent,
	AlarisBalanceDialogComponent,
	AlarisBalanceService,
	AlarisCostComponent,
	AlarisDialogComponent,
	AlarisDialogService,
	AlarisIconDirective,
	AlarisLinkComponent,
	AlarisNoValueCheckPipe,
	AlarisTagComponent,
	AlarisTextAreaComponent,
	AlarisTooltipDirective,
	ChannelUtilsService,
	TimeAgoPipe,
} from '@campaign-portal/components-library';
import { BaseActionDialogComponent, CanDeactivateGuardService } from '../../can-deactivate';
import { PurchaseService } from '../../services/purchase.service';
import { TranslateModule } from '@ngx-translate/core';
import { PackageCountryNetworkListComponent } from './package-country-network-list/package-country-network-list.component';
import { DecimalPipe } from '@angular/common';

export interface PurchaseDialogData {
	type: PurchaseDialogType;
	package?: Package<exist>;
	plan?: Plan<exist>;
}

export type PurchaseDialogType = 'PackageRequest' | 'PlanRequest' | 'SelectPlan' | 'ShopNow';

@Component({
	selector: 'app-dialogs',
	templateUrl: './dialogs.component.html',
	styleUrls: ['./dialogs.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AlarisDialogComponent,
		TranslateModule,
		AlarisTextAreaComponent,
		AlarisTagComponent,
		AlarisAlertComponent,
		AlarisIconDirective,
		AlarisLinkComponent,
		PackageCountryNetworkListComponent,
		AlarisTooltipDirective,
		AlarisNoValueCheckPipe,
		TimeAgoPipe,
		AlarisCostComponent,
		DecimalPipe,
	],
	standalone: true,
})
export class PurchaseDialogsComponent extends BaseActionDialogComponent {
	readonly package!: Package<exist>;
	readonly plan!: Plan<exist>;
	readonly comment = new FormControl('', { nonNullable: true });
	protected readonly TrafficType = TrafficType;

	constructor(
		@Inject(DIALOG_DATA) public readonly data: PurchaseDialogData,
		public readonly cu: ChannelUtilsService,
		public readonly bs: AlarisBalanceService,
		public readonly purchaseService: PurchaseService,
		private readonly dialogRef: DialogRef<unknown>,
		private readonly cd: ChangeDetectorRef,
		private readonly guard: CanDeactivateGuardService,
		private readonly dialog: AlarisDialogService
	) {
		super();
		this.addDialogGuard(this.dialog, dialogRef, this.guard);
		this.package = data.package as Package<exist>;
		this.plan = data.plan as Plan<exist>;
	}

	get positiveBalance(): boolean {
		return this.package
			? this.bs.credit === null || this.bs.balance + this.bs.credit >= this.package.packSettings.packPrice
			: true;
	}

	get confirmButtonText(): string {
		const buttonTextMap: Record<string, string> = {
			ShopNow: 'purchase.buyAction',
		};

		return buttonTextMap[this.data.type] ?? 'actions.confirm';
	}

	get disabled(): boolean {
		let comment = false;
		switch (this.data.type) {
			case 'PackageRequest':
			case 'PlanRequest':
				comment = this.comment.value.length > 0;
				break;
			case 'SelectPlan':
			case 'ShopNow':
				comment = this.positiveBalance;
				break;
			default:
				break;
		}
		return !this.loading$.getValue() && !comment;
	}

	seeAllCountries(): void {
		this.purchaseService.openAllCountriesDialog(this.plan || this.package);
	}

	close(): void {
		this.dialogRef.close(false);
	}

	topUp(): void {
		this.dialog.open(AlarisBalanceDialogComponent);
	}

	confirm(): void {
		if (this.disabled) {
			return;
		}

		let caller$: Observable<RPCResult<unknown>> | null = null;

		switch (this.data.type) {
			case 'PackageRequest':
				caller$ = this.purchaseService.request({
					Data: {
						type: SubscriptionType.PACK,
						comment: this.comment.value,
					},
				});
				break;
			case 'PlanRequest':
				caller$ = this.purchaseService.request({
					Data: {
						type: SubscriptionType.PLAN,
						comment: this.comment.value,
					},
				});
				break;
			case 'SelectPlan':
				caller$ = this.purchaseService.subscribe({
					Data: {
						Ids: [this.plan.id],
					},
				});
				break;
			case 'ShopNow':
				caller$ = this.purchaseService.subscribe({
					Data: {
						Ids: [this.package.id],
					},
				});
				break;
			default:
				break;
		}

		this.runAction(caller$, (resp) => {
			this.dialogRef.close(resp.Success);
			this.cd.markForCheck();
		});
	}
}
